export type Env =
  | 'local'
  | 'dev'
  | 'stg'
  | 'stg-fedm'
  | 'stg-ic'
  | 'prod'
  | 'prod-fedm'
  | 'prod-ic'
  | 'unknown';

export type EnvType = 'dev' | 'staging' | 'prod' | 'unknown';

export type EnvConfig = {
  recaptcha: {
    smsEnrollmentRecaptchaEnterpriseInvisibleKey: string;
    smsEnrollmentRecaptchaEnterpriseNewKey: string;
  };
};

function inferEnvFromHostname(hostname: string): Env {
  if (hostname === 'localhost') {
    return 'local';
  }

  if (hostname.endsWith('.dev.internal.atlassian.com') || hostname.endsWith('.dev.atlassian.com')) {
    return 'dev';
  }

  if (hostname.endsWith('.stg.internal.atlassian.com') || hostname.endsWith('.stg.atlassian.com')) {
    return 'stg';
  }

  if (hostname.endsWith('.stg.atlassian-us-gov-mod.com')) {
    return 'stg-fedm';
  }

  if (hostname.endsWith('.oasis-stg.com')) {
    return 'stg-ic';
  }

  if (hostname.endsWith('.atlassian-us-gov-mod.com')) {
    return 'prod-fedm';
  }

  if (hostname.endsWith('.atlassian.com')) {
    return 'prod';
  }

  if (hostname.endsWith('.atlassian-isolated.net')) {
    return 'prod-ic';
  }

  return 'unknown';
}

export const currentEnv: Env = inferEnvFromHostname(window.location.hostname);
export const getCurrentEnv = (): Env => inferEnvFromHostname(window.location.hostname);

const envTypes: Record<Env, EnvType> = {
  local: 'dev',
  dev: 'dev',
  stg: 'staging',
  'stg-fedm': 'staging',
  'stg-ic': 'staging',
  prod: 'prod',
  'prod-fedm': 'prod',
  'prod-ic': 'prod',
  unknown: 'unknown',
};
export const currentEnvType: EnvType = envTypes[currentEnv];

const idacOrigins: Record<Env, string> = {
  // The local proxy is created within Parcel on the frontend local step, and requests are proxied to staging
  // Using `window.location.protocol` means that we don't need to make a change here if we run using HTTPS or HTTP.
  local: `${window.location.protocol}//localhost:3000`, // idproxy local
  dev: 'https://id.dev.internal.atlassian.com',
  stg: 'https://id.stg.internal.atlassian.com',
  'stg-fedm': 'https://id.stg.atlassian-us-gov-mod.com',
  'stg-ic': window.location.origin, // dynamic based on the individual IC
  prod: 'https://id.atlassian.com',
  'prod-fedm': 'https://id.atlassian-us-gov-mod.com',
  'prod-ic': window.location.origin, // dynamic based on the individual IC
  unknown: 'https://id.atlassian.com', // safe default
};

export const idacOrigin = idacOrigins[currentEnv];

export type AtlassianCloudType = 'commercial' | 'fedramp-moderate' | 'isolated-cloud';

const atlassianCloudType: Record<Env, AtlassianCloudType> = {
  local: 'commercial',
  dev: 'commercial',
  stg: 'commercial',
  'stg-fedm': 'fedramp-moderate',
  'stg-ic': 'isolated-cloud',
  prod: 'commercial',
  'prod-fedm': 'fedramp-moderate',
  'prod-ic': 'isolated-cloud',
  unknown: 'commercial',
};

export type Perimeter = 'commercial' | 'fedramp-moderate' | 'fedramp-high';

const perimeter: Record<Env, Perimeter> = {
  local: 'commercial',
  dev: 'commercial',
  stg: 'commercial',
  'stg-fedm': 'fedramp-moderate',
  'stg-ic': 'commercial',
  prod: 'commercial',
  'prod-fedm': 'fedramp-moderate',
  'prod-ic': 'commercial',
  unknown: 'commercial',
};

const config: Record<EnvType, EnvConfig> = {
  dev: {
    recaptcha: {
      smsEnrollmentRecaptchaEnterpriseInvisibleKey: '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI', // no captcha site key
      smsEnrollmentRecaptchaEnterpriseNewKey: '6Ld_RrgpAAAAAC6NtADmI_b9uTwxyDieSQbL0A4O',
    },
  },
  staging: {
    recaptcha: {
      smsEnrollmentRecaptchaEnterpriseInvisibleKey: '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI', // no captcha site key
      smsEnrollmentRecaptchaEnterpriseNewKey: '6Ld_RrgpAAAAAC6NtADmI_b9uTwxyDieSQbL0A4O',
    },
  },
  prod: {
    recaptcha: {
      smsEnrollmentRecaptchaEnterpriseInvisibleKey: '6LeLRz0kAAAAAPIWGnreLcE0BJaZ17REyxkFhM0E',
      smsEnrollmentRecaptchaEnterpriseNewKey: '6LcjUrgpAAAAANvg2hgPsYsHNO9F21kQVCQwUPoF',
    },
  },
  unknown: {
    recaptcha: {
      smsEnrollmentRecaptchaEnterpriseInvisibleKey: '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI', // no captcha site key
      smsEnrollmentRecaptchaEnterpriseNewKey: '6Ld_RrgpAAAAAC6NtADmI_b9uTwxyDieSQbL0A4O',
    },
  },
};

export const currentCloudType = atlassianCloudType[currentEnv];
export const currentPerimeter = perimeter[currentEnv];
export const getCurrentCloudType = () => atlassianCloudType[currentEnv];
export const isFedM = () => getCurrentCloudType() === 'fedramp-moderate';
export const isIsolatedCloud = () => getCurrentCloudType() === 'isolated-cloud';
export const getConfig = () => config[currentEnvType];

export const isResetPasswordEnabled = () => {
  if (isIsolatedCloud()) {
    return false;
  }

  // FedRAMP: staging-first rollout — enabled in stg-fedm only, still disabled in prod-fedm.
  if (isFedM()) {
    return getCurrentEnv() === 'stg-fedm';
  }

  // Commercial (local/dev/stg/prod).
  return true;
};
